import { create } from "zustand";

export type RuntimeConversationActivityState = "idle" | "running" | "error";
export type RuntimeConversationStatusKind = "idle" | "running" | "waiting_input" | "error";
export type RuntimeConversationFinishReason =
  | "completed"
  | "error"
  | "aborted"
  | "retry_exhausted"
  | "waiting_input";

export interface RuntimeConversationActivitySnapshot {
  sessionId: string;
  conversationId: string;
  agentType: "opencode" | "claude_code" | "cursor";
  providerSessionId: string | null;
  title: string | null;
  isDefault: boolean;
  statusKind: RuntimeConversationStatusKind | null;
  activityState: RuntimeConversationActivityState | null;
  activityEventSequence: number | null;
  lastActivityEventId: number | null;
  lastFinishedEventId: number | null;
  lastFinishedAt: number | null;
  lastFinishReason: RuntimeConversationFinishReason | null;
  rawTailId?: number | null;
  terminalRawCursor?: number | null;
  terminalRawCursorObservedAt?: number | null;
  activityObservedAt: number | null;
  createdAt: number;
  updatedAt: number;
  archivedAt: number | null;
  timestamp?: number;
}

export interface RuntimeConversationActivityFreshnessSnapshot {
  statusKind?: RuntimeConversationStatusKind | null;
  activityState?: RuntimeConversationActivityState | null;
  activityEventSequence?: number | null;
  lastActivityEventId?: number | null;
  lastFinishedEventId?: number | null;
  lastFinishedAt?: number | null;
  rawTailId?: number | null;
  terminalRawCursor?: number | null;
  terminalRawCursorObservedAt?: number | null;
  updatedAt?: number | null;
  timestamp?: number | null;
}

type RuntimeConversationActivityFreshnessComparison =
  | "left_newer"
  | "right_newer"
  | "same";

interface RuntimeConversationActivityStore {
  bySessionId: Record<string, Record<string, RuntimeConversationActivitySnapshot>>;
  viewedEventIds: Record<string, number>;
  applySnapshots: (snapshots: RuntimeConversationActivitySnapshot[]) => void;
  replaceSessionSnapshots: (input: { sessionId: string; snapshots: RuntimeConversationActivitySnapshot[]; force?: boolean }) => void;
  markViewed: (input: { sessionId: string; conversationId: string }) => number | null;
  markViewedByEventId: (input: { sessionId: string; conversationId: string; viewedEventId: number }) => void;
  getLastViewedEventId: (input: { sessionId: string; conversationId: string }) => number;
  pruneToSessions: (sessionIds: string[]) => void;
}

const VIEWED_STORAGE_PREFIX = "cord-runtime-conversation-viewed-state:";
const MAX_PERSISTED_VIEWED_CONVERSATION_COUNT = 1_000;

// Runtime-conversation snapshots come from both the SessionDO query/websocket
// lane and the ActivityHub multiplex lane. The hub adds a publish timestamp that
// can be a few milliseconds newer than the semantic DB row it is describing, so
// activity event ids must be treated as the authoritative revision and wall-clock
// timestamps only as a metadata tie-breaker.
const NO_RUNTIME_CONVERSATION_ACTIVITY_REVISION = 0;

let viewedPersistenceScopeKey: string | null = null;

function viewedKey(input: { sessionId: string; conversationId: string }): string {
  return `${input.sessionId}:${input.conversationId}`;
}

function storagePrefix(scopeKey: string): string {
  return `${VIEWED_STORAGE_PREFIX}${scopeKey}:`;
}

function storageKey(input: { scopeKey: string; sessionId: string; conversationId: string }): string {
  return `${storagePrefix(input.scopeKey)}${viewedKey(input)}`;
}

function parseViewedStorageKey(input: { scopeKey: string; key: string | null }): { sessionId: string; conversationId: string } | null {
  if (!input.key?.startsWith(storagePrefix(input.scopeKey))) return null;
  const suffix = input.key.slice(storagePrefix(input.scopeKey).length);
  const separatorIndex = suffix.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex >= suffix.length - 1) return null;
  return {
    sessionId: suffix.slice(0, separatorIndex),
    conversationId: suffix.slice(separatorIndex + 1),
  };
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

function getSnapshotActivityRevision(snapshot: RuntimeConversationActivityFreshnessSnapshot): number | null {
  const revisions = [
    normalizePositiveInteger(snapshot.activityEventSequence),
    normalizePositiveInteger(snapshot.lastActivityEventId),
    normalizePositiveInteger(snapshot.lastFinishedEventId),
  ].filter((value): value is number => value !== null);
  if (revisions.length === NO_RUNTIME_CONVERSATION_ACTIVITY_REVISION) return null;
  return Math.max(...revisions);
}

function getSnapshotMetadataRevision(snapshot: RuntimeConversationActivityFreshnessSnapshot): number {
  return normalizePositiveInteger(snapshot.updatedAt) ??
    normalizePositiveInteger(snapshot.timestamp) ??
    NO_RUNTIME_CONVERSATION_ACTIVITY_REVISION;
}

export function runtimeConversationActivitySnapshotHasTerminalEvidence(
  snapshot: RuntimeConversationActivityFreshnessSnapshot | null | undefined,
): boolean {
  if (!snapshot) return false;
  const lastActivityEventId = normalizePositiveInteger(snapshot.lastActivityEventId);
  const lastFinishedEventId = normalizePositiveInteger(snapshot.lastFinishedEventId);
  return lastActivityEventId !== null &&
    lastFinishedEventId !== null &&
    lastFinishedEventId >= lastActivityEventId;
}

export function compareRuntimeConversationActivitySnapshotFreshness(args: {
  left: RuntimeConversationActivityFreshnessSnapshot;
  right: RuntimeConversationActivityFreshnessSnapshot;
}): RuntimeConversationActivityFreshnessComparison {
  const leftActivityRevision = getSnapshotActivityRevision(args.left);
  const rightActivityRevision = getSnapshotActivityRevision(args.right);

  if (leftActivityRevision !== null && rightActivityRevision !== null) {
    if (leftActivityRevision > rightActivityRevision) return "left_newer";
    if (rightActivityRevision > leftActivityRevision) return "right_newer";
  } else if (leftActivityRevision !== null) {
    return "left_newer";
  } else if (rightActivityRevision !== null) {
    return "right_newer";
  }

  const leftHasTerminalEvidence = runtimeConversationActivitySnapshotHasTerminalEvidence(args.left);
  const rightHasTerminalEvidence = runtimeConversationActivitySnapshotHasTerminalEvidence(args.right);
  if (leftHasTerminalEvidence !== rightHasTerminalEvidence) {
    return leftHasTerminalEvidence ? "left_newer" : "right_newer";
  }

  const leftMetadataRevision = getSnapshotMetadataRevision(args.left);
  const rightMetadataRevision = getSnapshotMetadataRevision(args.right);
  if (leftMetadataRevision > rightMetadataRevision) return "left_newer";
  if (rightMetadataRevision > leftMetadataRevision) return "right_newer";

  const leftTimestamp = normalizePositiveInteger(args.left.timestamp) ?? NO_RUNTIME_CONVERSATION_ACTIVITY_REVISION;
  const rightTimestamp = normalizePositiveInteger(args.right.timestamp) ?? NO_RUNTIME_CONVERSATION_ACTIVITY_REVISION;
  if (leftTimestamp > rightTimestamp) return "left_newer";
  if (rightTimestamp > leftTimestamp) return "right_newer";

  return "same";
}

function getFreshestSnapshot(
  snapshots: RuntimeConversationActivitySnapshot[],
): RuntimeConversationActivitySnapshot | null {
  let freshest: RuntimeConversationActivitySnapshot | null = null;
  for (const snapshot of snapshots) {
    if (!freshest) {
      freshest = snapshot;
      continue;
    }
    if (compareRuntimeConversationActivitySnapshotFreshness({
      left: snapshot,
      right: freshest,
    }) === "left_newer") {
      freshest = snapshot;
    }
  }
  return freshest;
}

function readPersistedViewedEventId(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return normalizePositiveInteger(parsed.lastViewedEventId);
  } catch {
    return null;
  }
}

function persistViewedEventId(input: { sessionId: string; conversationId: string; viewedEventId: number }): void {
  if (!viewedPersistenceScopeKey || typeof window === "undefined") return;
  const viewedEventId = normalizePositiveInteger(input.viewedEventId);
  if (viewedEventId === null) return;
  try {
    window.localStorage.setItem(storageKey({
      scopeKey: viewedPersistenceScopeKey,
      sessionId: input.sessionId,
      conversationId: input.conversationId,
    }), JSON.stringify({ lastViewedEventId: viewedEventId, updatedAt: Date.now() }));
  } catch {
    return;
  }

  try {
    const prefix = storagePrefix(viewedPersistenceScopeKey);
    const rows: Array<{ key: string; updatedAt: number }> = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) as Record<string, unknown> : null;
      rows.push({ key, updatedAt: normalizePositiveInteger(parsed?.updatedAt) ?? 0 });
    }
    for (const row of rows.sort((a, b) => b.updatedAt - a.updatedAt).slice(MAX_PERSISTED_VIEWED_CONVERSATION_COUNT)) {
      window.localStorage.removeItem(row.key);
    }
  } catch {
    // Ignore cleanup failures.
  }
}

export function hydrateRuntimeConversationViewedState(scopeKey: string | null): void {
  if (!scopeKey || typeof window === "undefined") return;
  const prefix = storagePrefix(scopeKey);
  const updates: Array<{ sessionId: string; conversationId: string; viewedEventId: number }> = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      const parsedKey = parseViewedStorageKey({ scopeKey, key });
      if (!parsedKey || !key?.startsWith(prefix)) continue;
      const viewedEventId = readPersistedViewedEventId(key);
      if (viewedEventId === null) continue;
      updates.push({ ...parsedKey, viewedEventId });
    }
  } catch {
    return;
  }
  for (const update of updates) {
    useRuntimeConversationActivity.getState().markViewedByEventId(update);
  }
}

export function configureRuntimeConversationViewedStatePersistence(scopeKey: string | null): void {
  const normalizedScopeKey = typeof scopeKey === "string" && scopeKey.trim().length > 0 ? scopeKey.trim() : null;
  if (viewedPersistenceScopeKey === normalizedScopeKey) return;
  viewedPersistenceScopeKey = normalizedScopeKey;
  useRuntimeConversationActivity.setState({ viewedEventIds: {} });
  hydrateRuntimeConversationViewedState(normalizedScopeKey);
}

export function isRuntimeConversationViewedStateStorageEventForScope(input: { scopeKey: string; event: StorageEvent }): boolean {
  return input.event.storageArea === window.localStorage && parseViewedStorageKey({ scopeKey: input.scopeKey, key: input.event.key }) !== null;
}

function shouldReplaceSnapshot(args: {
  current: RuntimeConversationActivitySnapshot | undefined;
  next: RuntimeConversationActivitySnapshot;
}): boolean {
  if (!args.current) return true;
  return compareRuntimeConversationActivitySnapshotFreshness({
    left: args.next,
    right: args.current,
  }) !== "right_newer";
}

function areRuntimeConversationActivitySnapshotsEqual(args: {
  left: RuntimeConversationActivitySnapshot | undefined;
  right: RuntimeConversationActivitySnapshot | undefined;
}): boolean {
  const { left, right } = args;
  if (left === right) return true;
  if (!left || !right) return false;
  return left.sessionId === right.sessionId &&
    left.conversationId === right.conversationId &&
    left.agentType === right.agentType &&
    left.providerSessionId === right.providerSessionId &&
    left.title === right.title &&
    left.isDefault === right.isDefault &&
    left.statusKind === right.statusKind &&
    left.activityState === right.activityState &&
    left.activityEventSequence === right.activityEventSequence &&
    left.lastActivityEventId === right.lastActivityEventId &&
    left.lastFinishedEventId === right.lastFinishedEventId &&
    left.lastFinishedAt === right.lastFinishedAt &&
    left.lastFinishReason === right.lastFinishReason &&
    left.rawTailId === right.rawTailId &&
    left.terminalRawCursor === right.terminalRawCursor &&
    left.terminalRawCursorObservedAt === right.terminalRawCursorObservedAt &&
    left.activityObservedAt === right.activityObservedAt &&
    left.createdAt === right.createdAt &&
    left.updatedAt === right.updatedAt &&
    left.archivedAt === right.archivedAt &&
    left.timestamp === right.timestamp;
}

export const useRuntimeConversationActivity = create<RuntimeConversationActivityStore>((set, get) => ({
  bySessionId: {},
  viewedEventIds: {},

  applySnapshots: (snapshots) => {
    if (!Array.isArray(snapshots) || snapshots.length === 0) return;
    set((state) => {
      let changed = false;
      const nextBySessionId: RuntimeConversationActivityStore["bySessionId"] = { ...state.bySessionId };

      for (const snapshot of snapshots) {
        const sessionId = snapshot.sessionId.trim();
        const conversationId = snapshot.conversationId.trim();
        if (!sessionId || !conversationId) continue;
        const currentSession = nextBySessionId[sessionId] ?? {};
        const current = currentSession[conversationId];
        if (!shouldReplaceSnapshot({ current, next: snapshot })) continue;
        nextBySessionId[sessionId] = {
          ...currentSession,
          [conversationId]: { ...snapshot, sessionId, conversationId },
        };
        changed = true;
      }

      return changed ? { bySessionId: nextBySessionId } : state;
    });
  },

  replaceSessionSnapshots: ({ sessionId, snapshots, force }) => {
    const normalizedSessionId = sessionId.trim();
    if (!normalizedSessionId) return;
    set((state) => {
      const nextSession: Record<string, RuntimeConversationActivitySnapshot> = {};
      for (const snapshot of snapshots) {
        const conversationId = snapshot.conversationId.trim();
        if (!conversationId || snapshot.archivedAt !== null) continue;
        nextSession[conversationId] = { ...snapshot, sessionId: normalizedSessionId, conversationId };
      }
      const currentSession = state.bySessionId[normalizedSessionId] ?? {};
      const incomingFreshest = getFreshestSnapshot(Object.values(nextSession));
      const currentFreshest = getFreshestSnapshot(Object.values(currentSession));
      if (
        force !== true &&
        incomingFreshest &&
        currentFreshest &&
        compareRuntimeConversationActivitySnapshotFreshness({
          left: currentFreshest,
          right: incomingFreshest,
        }) === "left_newer"
      ) {
        return state;
      }
      const currentKeys = Object.keys(currentSession);
      const nextKeys = Object.keys(nextSession);
      const sameKeys = currentKeys.length === nextKeys.length && nextKeys.every((key) => {
        return areRuntimeConversationActivitySnapshotsEqual({
          left: currentSession[key],
          right: nextSession[key],
        });
      });
      if (sameKeys) return state;
      return {
        bySessionId: {
          ...state.bySessionId,
          [normalizedSessionId]: nextSession,
        },
      };
    });
  },

  markViewed: ({ sessionId, conversationId }) => {
    const snapshot = get().bySessionId[sessionId]?.[conversationId];
    const viewedEventId = normalizePositiveInteger(snapshot?.lastActivityEventId) ??
      normalizePositiveInteger(snapshot?.lastFinishedEventId);
    if (viewedEventId === null) return null;
    get().markViewedByEventId({ sessionId, conversationId, viewedEventId });
    return viewedEventId;
  },

  markViewedByEventId: ({ sessionId, conversationId, viewedEventId }) => {
    const normalizedViewedEventId = normalizePositiveInteger(viewedEventId);
    if (normalizedViewedEventId === null) return;
    set((state) => {
      const key = viewedKey({ sessionId, conversationId });
      const current = state.viewedEventIds[key] ?? 0;
      if (normalizedViewedEventId <= current) return state;
      return {
        viewedEventIds: {
          ...state.viewedEventIds,
          [key]: normalizedViewedEventId,
        },
      };
    });
    persistViewedEventId({ sessionId, conversationId, viewedEventId: normalizedViewedEventId });
  },

  getLastViewedEventId: ({ sessionId, conversationId }) => {
    return get().viewedEventIds[viewedKey({ sessionId, conversationId })] ?? 0;
  },

  pruneToSessions: (sessionIds) => {
    const allowed = new Set(sessionIds);
    set((state) => {
      let changed = false;
      const nextBySessionId: RuntimeConversationActivityStore["bySessionId"] = {};
      for (const [sessionId, conversations] of Object.entries(state.bySessionId)) {
        if (!allowed.has(sessionId)) {
          changed = true;
          continue;
        }
        nextBySessionId[sessionId] = conversations;
      }
      return changed ? { bySessionId: nextBySessionId } : state;
    });
  },
}));
